import React, { useRef } from "react"
import { Stage, Layer, Line, Rect, Circle, Text } from 'react-konva'
import { useKonva } from "../context/KonvaContext"
import { drawGrid } from "../utils/grid"
import { v4 as uuidv4 } from 'uuid'


const KonvaStage = () => {
    const isDrawing = useRef(false)
    const stageRef = useRef(null)
    const { tool,
        setTool,
        shapes,
        setShapes,
        scale,
        handleShapeClick,
        shapeAreas,
        shapesHistory,
        setShapesHistory } = useKonva()

    const createBoundingBox = (minX, minY, maxX, maxY, boundingBoxStyle, childId) => {
        return {
            id: uuidv4(),
            childId: childId,
            type: 'rectangle',
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            stroke: boundingBoxStyle.stroke,
            strokeWidth: boundingBoxStyle.strokeWidth,
            dash: boundingBoxStyle.dash, // Specify dash and gap lengths
            opacity: 0.5, // Make the bounding box transparent
        }
    }

    const boundingBoxStyle = {
        stroke: 'rgba(0, 0, 0, 0.5)', // Lighter stroke color
        strokeWidth: 1, // Decrease stroke width
        dash: [5, 5], // Dashed style [dashLength, gapLength]
    }

    const handleMouseDown = (e) => {
        isDrawing.current = true
        const stage = e.target.getStage()
        const pos = stage.getPointerPosition()

        let newShape
        switch (tool) {
            case 'pen':
                newShape = { id: uuidv4(), type: 'line', points: [pos.x, pos.y], tool: 'pen' }
                break
            case 'square':
                newShape = { id: uuidv4(), type: 'square', x: pos.x, y: pos.y, size: 0 }
                break
            case 'rectangle':
                newShape = {
                    id: uuidv4(),
                    type: 'rectangle',
                    x: pos.x,
                    y: pos.y,
                    width: 0,
                    height: 0,
                }
                break
            case 'circle':
                newShape = { id: uuidv4(), type: 'circle', x: pos.x, y: pos.y, radius: 0 }
                break
            case 'triangle':
                newShape = {
                    id: uuidv4(),
                    type: 'triangle',
                    points: [pos.x, pos.y, pos.x, pos.y, pos.x, pos.y],
                    x: pos.x,
                    y: pos.y,
                }
                break
            case 'LShape':
                newShape = {
                    id: uuidv4(),
                    type: 'LShape',
                    points: [pos.x, pos.y, pos.x, pos.y],
                    x: pos.x,
                    y: pos.y,
                }
                break
            default:
                return
        }
        setShapes([...shapes, newShape])
    }

    const handleMouseMove = (e) => {
        if (!isDrawing.current) return

        const stage = e.target.getStage()
        const pos = stage.getPointerPosition()
        let lastShape = shapes[shapes.length - 1]

        switch (tool) {
            case 'pen':
                lastShape.points = lastShape.points.concat([pos.x, pos.y])
                break
            case 'square':
                lastShape.size = Math.max(
                    pos.x - lastShape.x,
                    pos.y - lastShape.y
                )
                break
            case 'rectangle':
                lastShape.width = pos.x - lastShape.x
                lastShape.height = pos.y - lastShape.x
                break
            case 'circle':
                lastShape.radius = Math.sqrt(
                    Math.pow(pos.x - lastShape.x, 2) +
                    Math.pow(pos.y - lastShape.y, 2)
                )
                break
            case 'triangle':
                lastShape.points = [
                    lastShape.points[0],
                    lastShape.points[1], // fixed first point
                    pos.x,
                    pos.y,
                    lastShape.points[0],
                    pos.y,
                ]
                break
            case 'LShape':
                lastShape.points[2] = pos.x
                lastShape.points[3] = lastShape.points[1]
                lastShape.points[4] = pos.x
                lastShape.points[5] = pos.y
                lastShape.points[6] = lastShape.points[0]
                lastShape.points[7] = pos.y
                lastShape.points[8] = lastShape.points[0]
                lastShape.points[9] = lastShape.points[1]
                break
            default:
                return
        }

        shapes.splice(shapes.length - 1, 1, lastShape)
        setShapes([...shapes])
    }

    const handleMouseUp = () => {
        isDrawing.current = false
        const lastShape = shapes[shapes.length - 1]
        const lastShapeId = lastShape?.id

        if (tool === 'pen') {
            const lastShape = shapes[shapes.length - 1]

            // Calculate bounding box of the free-drawn shape
            const minX = Math.min(
                ...lastShape.points.filter((_, i) => i % 2 === 0)
            )
            const minY = Math.min(
                ...lastShape.points.filter((_, i) => i % 2 === 1)
            )
            const maxX = Math.max(
                ...lastShape.points.filter((_, i) => i % 2 === 0)
            )
            const maxY = Math.max(
                ...lastShape.points.filter((_, i) => i % 2 === 1)
            )

            const boundingBox = createBoundingBox(
                minX,
                minY,
                maxX,
                maxY,
                boundingBoxStyle,
                lastShape.id
            )

            setShapes([...shapes, boundingBox])
            setShapesHistory((prev) => ({
                ...prev,
                [boundingBox.id]: {
                    shape: [lastShape, boundingBox],
                }
            }))
            // setShapes([...shapes, rectangle]);
        } else {
            if (!shapesHistory[lastShapeId]) {
                setShapesHistory((prev) => ({
                    ...prev,
                    [lastShapeId]: {
                        shape: [lastShape]
                    }
                }))
            }
        }
        setTool(null)
    }

    const handleShapeMouseEnter = (e) => {
        e.target.getStage().container().style.cursor = 'pointer'
    }

    const handleShapeMouseLeave = (e) => {
        e.target.getStage().container().style.cursor = 'default'
    }

    const getShape = (entryId) => {
        return shapes.find(({ id }) => id === entryId)
    }

    const getXPosition = (entryId) => {
        const shape = getShape(entryId)
        if (!shape) return 0

        return shape.x
    }

    const getYPosition = (entryId) => {
        const shape = getShape(entryId)
        if (!shape) return 0

        return shape.y - 20
    }

    const handleDragStart = (e) => {
        e.target.moveToTop()
    }

    const handleDragEnd = (e, shape) => {

        const stage = e.target.getStage()
        const { id, type } = shape
        let pos = null

        if (type === 'triangle' || type === 'LShape') {
            pos = stage.getPointerPosition()
        } else {
            pos = e.target.position()
        }

        const updatedShapes = shapes.map((shape) => {
            if (shape.id === id) {
                return { ...shape, x: pos.x, y: pos.y }
            }
            return shape
        })

        setShapes(updatedShapes)
    }


    return (
        <Stage
            width={window.innerWidth}
            height={window.innerHeight}
            onMouseDown={handleMouseDown}
            onMousemove={handleMouseMove}
            onMouseup={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            scaleX={scale}
            scaleY={scale}
            ref={stageRef}
            style={{touchAction:'none'}}
        >
            <Layer>
                {drawGrid()}
                {shapes.map((shape) => {
                    switch (shape.type) {
                        case 'line':
                            return (
                                <Line
                                    key={shape.id}
                                    points={shape.points}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    closed
                                    draggable
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        case 'square':
                            return (
                                <Rect
                                    key={shape.id}
                                    x={shape.x}
                                    y={shape.y}
                                    width={shape.size}
                                    height={shape.size}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    draggable
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        case 'rectangle':
                            return (
                                <Rect
                                    key={shape.id}
                                    x={shape.x}
                                    y={shape.y}
                                    width={shape.width}
                                    height={shape.height}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    draggable={!shape.childId}
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        case 'circle':
                            return (
                                <Circle
                                    key={shape.id}
                                    x={shape.x}
                                    y={shape.y}
                                    radius={shape.radius}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    draggable
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        case 'triangle':
                            return (
                                <Line
                                    key={shape.id}
                                    points={shape.points}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    closed
                                    draggable
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        case 'LShape':
                            return (
                                <Line
                                    key={shape.id}
                                    points={shape.points}
                                    stroke="#0b3d2c"
                                    strokeWidth={2}
                                    closed
                                    draggable
                                    onDragStart={handleDragStart}
                                    onDragEnd={(e) => handleDragEnd(e, shape)}
                                    onClick={() => handleShapeClick(shape.id)}
                                    onTap={() => handleShapeClick(shape.id)}
                                    onMouseEnter={handleShapeMouseEnter}
                                    onMouseLeave={handleShapeMouseLeave}
                                />
                            )
                        default:
                            return null
                    }
                })}
                {Object.entries(shapeAreas).map(([id, area], index) => (
                    <Text
                        key={`area-${id}`}
                        text={
                            shapeAreas[id]
                                ? `${Number(index) + 1}. Area: ${shapeAreas[id].toFixed(
                                    2
                                )} sq ft`
                                : ''
                        }
                        x={getXPosition(id)}
                        y={getYPosition(id)}
                        fontSize={15}
                        fill="black"
                    />
                ))}
            </Layer>
        </Stage>
    )
}

export default KonvaStage